import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import { useTopLoader } from "../context/TopLoaderContext";
import Alert from "../components/Alert.jsx";

const PersonalityList = () => {
  const [loading, setLoading] = useState(true);
  const [personalities, setPersonalities] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const { start, stop } = useTopLoader();
  const navigate = useNavigate();

  // Alert State
  const [alert, setAlert] = useState({ show: false, message: "", type: "success" });

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
  };

  useEffect(() => {
    fetchPersonalities();
  }, []);

  const fetchPersonalities = async () => {
    try {
      start();
      const res = await api.get(URLS.GET_PERSONALITY_LIST);
      setPersonalities(res.data);
    } catch (err) {
      console.error("Error loading personalities:", err);
      showAlert("Failed to load personalities", "error");
    } finally {
      setLoading(false);
      stop();
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(personalities.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteBulk = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} personalities?`)) return;

    try {
      start();
      await api.delete(URLS.DELETE_PERSONALITY_BULK, { data: { ids: selectedIds } });
      showAlert(`${selectedIds.length} personalities deleted successfully`);
      setSelectedIds([]);
      fetchPersonalities();
    } catch (err) {
      console.error("Bulk delete error:", err);
      showAlert("Failed to delete personalities", "error");
    } finally {
      stop();
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="p-6 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">Personality Types</h1>
        {selectedIds.length > 0 && (
          <button
            onClick={handleDeleteBulk}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
              <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
            </svg>
            Delete Selected ({selectedIds.length})
          </button>
        )}
      </div>

      <div className="shadow-md rounded-lg overflow-hidden border border-gray-200 bg-white">
        <table className="min-w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-3 w-10">
                <input
                  type="checkbox"
                  className="rounded"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === personalities.length && personalities.length > 0}
                />
              </th>
              <th className="p-3 font-medium text-gray-600">ID</th>
              <th className="p-3 font-medium text-gray-600">Title</th>
              <th className="p-3 font-medium text-gray-600">Code</th>
              <th className="p-3 font-medium text-gray-600">Image</th>
              <th className="p-3 font-medium text-gray-600">Icon</th>
              <th className="p-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>

          <tbody>
            {personalities.map((p) => (
              <tr
                key={p.id}
                className={`border-b hover:bg-gray-50 transition ${selectedIds.includes(p.id) ? 'bg-blue-50' : ''}`}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={selectedIds.includes(p.id)}
                    onChange={() => handleSelectOne(p.id)}
                  />
                </td>
                <td className="p-3">{p.id}</td>

                <td className="p-3 font-medium">{p.title}</td>

                <td className="p-3">
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-bold uppercase">
                    {p.code || "N/A"}
                  </span>
                </td>

                <td className="p-3">
                  {p.image_url ? (
                    <img
                      src={ImgURL(p.image_thumb_150 || p.image_url)}
                      alt={p.title}
                      className="w-auto h-10 rounded-md border"
                    />
                  ) : (
                    <span className="text-gray-400 italic">No Image</span>
                  )}
                </td>

                <td className="p-3">
                  {p.icon_url ? (
                    <img
                      src={ImgURL(p.icon_thumb_150 || p.icon_url)}
                      alt="Icon"
                      className="w-10 h-10 object-contain rounded-md border p-1 bg-gray-50"
                    />
                  ) : (
                    <span className="text-gray-400 italic">No Icon</span>
                  )}
                </td>

                <td className="p-3 space-x-3">
                  <button
                    className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition"
                    onClick={() => navigate(`/admin/personalities/edit/${p.id}`)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {alert.show && (
        <Alert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      )}
    </div>
  );
};

export default PersonalityList;
